import { type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
    type ExcalidrawImageElement,
    type FileId,
    type NonDeletedExcalidrawElement
} from '@excalidraw/excalidraw/types/element/types';
import { type BinaryFileData, type DataURL, type AppState, type ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types/types';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, FormControlLabel, Slider, Stack, Switch, Typography, styled } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import ColorPickerButton, { type ColorPickerButtonProps } from './ColorPickerButton';
import { type LayerButtonProps, LayerButtons } from './LayerButtons';
import Library from './Library';
import { usePrevious } from '../../../../../utils/usePrevious';
import { WhiteboardContext } from '../../../context/WhiteboardContext';
import { WhiteboardSessionContext } from '../../../context/WhiteboardSessionContext';
import { type LibraryGroup } from '../../../types';
import { CODING_ERROR_OPTION_NOT_SUPPORTED } from '../../../whiteboard-errors';
import WhiteboardError from '../../../WhiteboardError';
import { toolTypes } from '../ElementTools';

interface ImageElementWithKey extends ExcalidrawImageElement {
    key: string;
}

const toolTypesByValue: any = {};
toolTypes.forEach((toolType) => {
    if (toolType.value) {
        toolTypesByValue[toolType.value!] = toolType;
    } else if (toolType.group) {
        toolType.tools.forEach((groupToolType) => {
            toolTypesByValue[groupToolType.value!] = groupToolType;
        });
    }
});

const ShowAdvancedOptionsButton = styled(Box)`
    font-size: 10px;
    position: relative;
    width: 220px;
    &:hover {
        cursor: pointer;
    }
`;

const ShowAdvancedOptionsButtonIcon = styled(ExpandMoreIcon)`
    width: 14px;
    height: 14px;
    position: absolute;
    right: 4px;
    top: 4px;
`;

const HideAdvancedOptionsButton = styled(Box)`
    font-size: 10px;
    position: relative;
    width: 220px;
    &:hover {
        cursor: pointer;
    }
`;

const HideAdvancedOptionsButtonIcon = styled(ExpandLessIcon)`
    width: 14px;
    height: 14px;
    position: absolute;
    right: 4px;
    top: 4px;
`;

const useStyles = makeStyles(() => ({
    root: {
        position: 'relative',
        overflowY: 'auto',
        width: '214px',
        overflowX: 'hidden',
        flexShrink: 0
    }
}));

const changeAppState = (excalidrawAPI: ExcalidrawImperativeAPI, property: string, value: any) => {
    excalidrawAPI.updateScene({
        // @ts-ignore
        appState: {
            // @ts-ignore
            [property]: value
        }
    });
};

const changeElements = (excalidrawAPI: ExcalidrawImperativeAPI, property: string, value: any) => {
    const selectedElementIds = Object.keys(excalidrawAPI.getAppState().selectedElementIds).filter(
        (id) => id !== excalidrawAPI.getAppState().selectionElement?.id
    );

    const elements = excalidrawAPI.getSceneElements().map((element) => {
        if (selectedElementIds.includes(element.id)) {
            return { ...element, [property]: value, version: element.version + 1 };
        }
        return element;
    });
    excalidrawAPI.updateScene({ elements });
};

const standardStrokeColors: string[] = [
    '#000000',
    '#343a40',
    '#495057',
    '#c92a2a',
    '#a61e4d',
    '#862e9c',
    '#5f3dc4',
    '#364fc7',
    '#1864ab',
    '#0b7285',
    '#087f5b',
    '#2b8a3e',
    '#5c940d',
    '#e67700',
    '#d9480f'
];

const standardBackgroundColors: string[] = [
    'transparent',
    '#ced4da',
    '#868e96',
    '#fa5252',
    '#e64980',
    '#be4bdb',
    '#7950f2',
    '#4c6ef5',
    '#228be6',
    '#15aabf',
    '#12b886',
    '#40c057',
    '#82c91e',
    '#fab005',
    '#fd7e14'
];

/**
 * Excalidraw's tools, excluding custom tools like library/math symbols.
 */
const standardElementTypes = ['rectangle', 'diamond', 'ellipse', 'arrow', 'line', 'freedraw', 'text'];

const optionsInfo: {
    [optionName: string]: {
        stateName: string;
        supportedElementTypes: string[];
        unsupportedElementTypes?: string[];
        buttons?: any[];
        buttonSx?: any;
        advanced?: boolean;
    };
} = {
    strokeColor: {
        stateName: 'currentItemStrokeColor',
        supportedElementTypes: standardElementTypes,
        advanced: false
    },
    strokeStyle: {
        stateName: 'currentItemStrokeStyle',
        buttons: [
            {
                value: 'solid',
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 36 4" fill="none">
                        <rect width="30" height="4" fill="#666666" />
                    </svg>
                )
            },
            {
                value: 'dashed',
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 40 4" fill="none">
                        <rect width="18" height="4" fill="#666666" />
                        <rect x="22" width="18" height="4" fill="#666666" />
                    </svg>
                )
            },
            {
                value: 'dotted',
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 36 4" fill="none">
                        <rect width="6" height="4" fill="#666666" />
                        <rect x="10" width="6" height="4" fill="#666666" />
                        <rect x="20" width="6" height="4" fill="#666666" />
                        <rect x="30" width="6" height="4" fill="#666666" />
                    </svg>
                )
            }
        ],
        supportedElementTypes: ['rectangle', 'diamond', 'ellipse', 'arrow', 'line'],
        advanced: true
    },
    strokeWidth: {
        stateName: 'currentItemStrokeWidth',
        buttons: [
            {
                value: 1,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 36 4" fill="none">
                        <rect width="30" height="4" rx="2" fill="#666666" />
                    </svg>
                )
            },
            {
                value: 2,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="6" viewBox="0 0 36 6" fill="none">
                        <rect width="30" height="6" rx="3" fill="#666666" />
                    </svg>
                )
            },
            {
                value: 4,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="8" viewBox="0 0 36 8" fill="none">
                        <rect width="30" height="8" rx="4" fill="#666666" />
                    </svg>
                )
            }
        ],
        supportedElementTypes: ['rectangle', 'diamond', 'ellipse', 'arrow', 'line', 'freedraw'],
        advanced: true
    },
    backgroundColor: {
        stateName: 'currentItemBackgroundColor',
        supportedElementTypes: ['rectangle', 'diamond', 'ellipse'],
        advanced: false
    },
    fillStyle: {
        stateName: 'currentItemFillStyle',
        buttons: [
            {
                value: 'hachure',
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 39 19" fill="none">
                        <g>
                            <path
                                d="M11.1179 0.632573L13.7717 0.479248L0.831773 13.9192L0.984931 11.266L11.1179 0.632573Z"
                                fill="#666666"
                            />
                            <path
                                d="M21.3817 0.368769L24.5 0.250488L6.71862 17.5319L4.12006 17.6304L21.3817 0.368769Z"
                                fill="#666666"
                            />
                            <path
                                d="M29.949 0.800536L32.999 0.750488L15.9496 17.8006L13.4079 17.8423L29.949 0.800536Z"
                                fill="#666666"
                            />
                            <path d="M38.5 2.25049L38.5 5.25049L26 17.7505L23 17.7505L38.5 2.25049Z" fill="#666666" />
                        </g>
                        <rect x="0.5" y="0.5" width="37.9045" height="17.5004" stroke="#666666" fill="none" />
                    </svg>
                )
            },
            {
                value: 'cross-hatch',
                icon: (
                    <svg width="30" height="25" viewBox="0 0 39 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g id="Frame 1120">
                            <g>
                                <path
                                    id="Rectangle 965"
                                    d="M11.1179 1.38208L13.7717 1.22876L0.831773 14.6687L0.984931 12.0156L11.1179 1.38208Z"
                                    fill="#666666"
                                />
                                <path
                                    id="Rectangle 971"
                                    d="M2.19017 1.33617L-1.35571 0.290772L19.9675 22.1139L18.9225 18.569L2.19017 1.33617Z"
                                    fill="#666666"
                                />
                                <path
                                    id="Rectangle 966"
                                    d="M21.3817 1.11828L24.5 1L6.71862 18.2814L4.12006 18.3799L21.3817 1.11828Z"
                                    fill="#666666"
                                />
                                <path
                                    id="Rectangle 970"
                                    d="M13.1793 1.17928L10 1L28.4422 18.9422L31.0916 19.0916L13.1793 1.17928Z"
                                    fill="#666666"
                                />
                                <path
                                    id="Rectangle 967"
                                    d="M29.949 1.55005L32.999 1.5L15.9496 18.5501L13.4079 18.5918L29.949 1.55005Z"
                                    fill="#666666"
                                />
                                <path
                                    id="Rectangle 969"
                                    d="M23.2857 -0.278311L20 -0.563965L39.641 19.0777L42.379 19.3158L23.2857 -0.278311Z"
                                    fill="#666666"
                                />
                                <path id="Rectangle 968" d="M38.5 3L38.5 6L26 18.5L23 18.5L38.5 3Z" fill="#666666" />
                            </g>
                            <rect x="0.5" y="1.24951" width="37.9045" height="17.5004" stroke="#666666" />
                        </g>
                    </svg>
                )
            },
            {
                value: 'solid',
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 36 16" fill="none">
                        <rect x="0.5" y="0.5" width="35" height="15" fill="#666666" stroke="#9E9E9E" />
                    </svg>
                )
            }
        ],
        supportedElementTypes: ['rectangle', 'diamond', 'ellipse'],
        advanced: false
    },
    opacity: {
        stateName: 'currentItemOpacity',
        supportedElementTypes: [...standardElementTypes, 'image'],
        advanced: true
    },
    fontFamily: {
        stateName: 'currentItemFontFamily',
        supportedElementTypes: ['text'],
        buttons: [
            {
                value: 1,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 30 23" fill="none">
                        <path
                            d="M0.83 21.99C0.61 21.87 0.45 21.76 0.35 21.66C0.27 21.56 0.23 21.43 0.23 21.27C0.23 21.07 0.31 20.79 0.47 20.43C0.65 20.05 0.95 19.46 1.37 18.66C1.49 18.4 1.72 17.94 2.06 17.28C2.92 15.64 3.35 14.43 3.35 13.65C3.33 13.61 3.32 13.55 3.32 13.47C3.32 13.27 3.48 13.09 3.8 12.93C3.86 12.87 4.07 12.7 4.43 12.42C4.81 12.14 5.11 11.82 5.33 11.46C5.59 10.86 5.92 10.2 6.32 9.48C6.72 8.74 7.41 7.54 8.39 5.88C9.35 4.12 10.37 2.53 11.45 1.11C11.65 0.829999 11.87 0.609998 12.11 0.449998C12.37 0.289999 12.59 0.209999 12.77 0.209999C13.39 0.209999 14.02 0.449999 14.66 0.929999C15.32 1.41 15.75 1.89 15.95 2.37C16.03 2.51 16.07 2.81 16.07 3.27C16.07 3.89 16 4.94 15.86 6.42C15.82 6.68 15.78 7.09 15.74 7.65C15.68 8.55 15.59 9.51 15.47 10.53C15.25 12.77 15.09 14.52 14.99 15.78C14.89 17.04 14.84 18.22 14.84 19.32C14.84 19.88 14.85 20.29 14.87 20.55V20.76L14.84 21.45C14.72 21.57 14.48 21.63 14.12 21.63C13.72 21.63 13.32 21.56 12.92 21.42C12.52 21.3 12.3 21.15 12.26 20.97C12.24 20.95 12.21 20.93 12.17 20.91C12.13 20.89 12.11 20.8 12.11 20.64L12.14 19.98L12.23 18.3L12.38 13.71L11.99 13.74C11.59 13.74 10.74 13.82 9.44 13.98C8.16 14.14 7.35 14.27 7.01 14.37C6.83 14.37 6.61 14.49 6.35 14.73C6.09 14.97 5.86 15.28 5.66 15.66C5.6 15.86 5.46 16.15 5.24 16.53C5.02 16.89 4.91 17.1 4.91 17.16C4.79 17.24 4.68 17.35 4.58 17.49C4.5 17.61 4.45 17.69 4.43 17.73C4.43 17.95 4.23 18.52 3.83 19.44C3.45 20.34 3.07 21.08 2.69 21.66L1.88 22.8L0.83 21.99ZM8 11.67C8.14 11.65 8.42 11.61 8.84 11.55C9.26 11.47 9.63 11.42 9.95 11.4C10.91 11.16 11.57 11.04 11.93 11.04C12.13 11.04 12.27 11.06 12.35 11.1C12.41 11.18 12.47 11.24 12.53 11.28C12.61 11.3 12.66 11.31 12.68 11.31C12.72 11.15 12.88 10.06 13.16 8.04C13.44 6.02 13.58 4.78 13.58 4.32C13.58 4 13.57 3.73 13.55 3.51C13.53 3.27 13.51 3.12 13.49 3.06C13.31 3.06 12.9 3.54 12.26 4.5C11.62 5.44 11 6.51 10.4 7.71C9.94 8.53 9.62 8.94 9.44 8.94C9.3 9.08 9 9.55 8.54 10.35C8.08 11.15 7.9 11.59 8 11.67ZM18.1423 20.37C18.0823 20.25 17.9123 20.09 17.6323 19.89C17.3523 19.69 17.2123 19.57 17.2123 19.53C17.2123 19.45 17.1623 19.34 17.0623 19.2C16.9823 19.04 16.9023 18.9 16.8223 18.78C16.6623 18.62 16.7723 18.1 17.1523 17.22C17.5323 16.34 18.0423 15.3 18.6823 14.1C18.8023 13.98 18.9523 13.77 19.1323 13.47C19.3323 13.17 19.4623 13 19.5223 12.96C19.5223 12.88 19.5523 12.81 19.6123 12.75C19.6723 12.67 19.7423 12.63 19.8223 12.63L20.0623 12.15C20.1423 11.97 20.5323 11.54 21.2323 10.86C21.9323 10.18 22.4823 9.72 22.8823 9.48C23.2623 9.22 23.6723 9.09 24.1123 9.09C24.5923 9.09 25.0023 9.23 25.3423 9.51L26.1523 10.2C26.7523 10.7 27.0923 10.99 27.1723 11.07L28.2823 12.06L27.7723 13.56C27.5923 14.1 27.5023 14.61 27.5023 15.09C27.5023 15.85 27.7123 16.56 28.1323 17.22C28.2323 17.44 28.3623 17.61 28.5223 17.73C28.7023 17.85 28.9323 17.93 29.2123 17.97C29.6523 18.05 29.8723 18.27 29.8723 18.63C29.8723 19.07 29.7923 19.4 29.6323 19.62C29.4723 19.84 29.2823 19.98 29.0623 20.04C28.7423 20.08 28.5223 20.1 28.4023 20.1C27.8223 20.1 27.2923 19.95 26.8123 19.65C26.3323 19.35 25.9123 18.84 25.5523 18.12C25.4723 18 25.3623 17.78 25.2223 17.46C25.1023 17.14 25.0323 16.92 25.0123 16.8C24.8723 17 24.6423 17.26 24.3223 17.58C22.2623 19.66 20.5923 20.7 19.3123 20.7C18.8723 20.7 18.4823 20.59 18.1423 20.37ZM19.7923 17.88C20.1123 17.92 20.7323 17.56 21.6523 16.8C21.7523 16.72 21.8623 16.63 21.9823 16.53C22.1023 16.41 22.2323 16.29 22.3723 16.17C22.9323 15.71 23.3623 15.33 23.6623 15.03C23.9823 14.71 24.3023 14.33 24.6223 13.89L25.2823 12.81C25.0823 12.37 24.9023 12.08 24.7423 11.94C24.5823 11.78 24.4223 11.7 24.2623 11.7C23.8623 11.7 23.3623 12.06 22.7623 12.78C22.1623 13.48 21.4423 14.55 20.6023 15.99C20.3823 16.35 20.2023 16.73 20.0623 17.13C19.9223 17.51 19.8323 17.76 19.7923 17.88Z"
                            fill="#666666"
                        />
                    </svg>
                )
            },
            {
                value: 2,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 37 23" fill="none">
                        <path
                            d="M0.956055 22L9.20312 0.525391H12.2646L21.0537 22H17.8164L15.3115 15.4961H6.33203L3.97363 22H0.956055ZM7.15234 13.1816H14.4326L12.1914 7.23438C11.5078 5.42773 11 3.94336 10.668 2.78125C10.3945 4.1582 10.0088 5.52539 9.51074 6.88281L7.15234 13.1816ZM33.2387 20.0811C32.2621 20.9111 31.3197 21.4971 30.4115 21.8389C29.5131 22.1807 28.5463 22.3516 27.5111 22.3516C25.8021 22.3516 24.4887 21.9365 23.5707 21.1064C22.6527 20.2666 22.1938 19.1973 22.1938 17.8984C22.1938 17.1367 22.3646 16.4434 22.7064 15.8184C23.058 15.1836 23.5121 14.6758 24.0688 14.2949C24.6352 13.9141 25.2699 13.626 25.973 13.4307C26.4906 13.2939 27.2719 13.1621 28.3168 13.0352C30.4457 12.7812 32.0131 12.4785 33.0189 12.127C33.0287 11.7656 33.0336 11.5361 33.0336 11.4385C33.0336 10.3643 32.7846 9.60742 32.2865 9.16797C31.6127 8.57227 30.6117 8.27441 29.2836 8.27441C28.0434 8.27441 27.1254 8.49414 26.5297 8.93359C25.9438 9.36328 25.5092 10.1299 25.226 11.2334L22.6479 10.8818C22.8822 9.77832 23.268 8.88965 23.8051 8.21582C24.3422 7.53223 25.1186 7.00977 26.1342 6.64844C27.1498 6.27734 28.3266 6.0918 29.6645 6.0918C30.9926 6.0918 32.0717 6.24805 32.9018 6.56055C33.7318 6.87305 34.3422 7.26855 34.7328 7.74707C35.1234 8.21582 35.3969 8.81152 35.5531 9.53418C35.641 9.9834 35.685 10.7939 35.685 11.9658V15.4814C35.685 17.9326 35.7387 19.4854 35.8461 20.1396C35.9633 20.7842 36.1879 21.4043 36.5199 22H33.766C33.4926 21.4531 33.3168 20.8135 33.2387 20.0811ZM33.0189 14.1924C32.0619 14.583 30.6264 14.915 28.7123 15.1885C27.6283 15.3447 26.8617 15.5205 26.4125 15.7158C25.9633 15.9111 25.6166 16.1992 25.3725 16.5801C25.1283 16.9512 25.0063 17.3662 25.0063 17.8252C25.0063 18.5283 25.2699 19.1143 25.7973 19.583C26.3344 20.0518 27.1156 20.2861 28.141 20.2861C29.1566 20.2861 30.06 20.0664 30.851 19.627C31.642 19.1777 32.223 18.5674 32.5941 17.7959C32.8773 17.2002 33.0189 16.3213 33.0189 15.1592V14.1924Z"
                            fill="#666666"
                        />
                    </svg>
                )
            },
            {
                value: 3,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 28 21" fill="none">
                        <path
                            d="M9.54 0.349999L15.57 20H12.81L11.52 15.44H4.29L3 20H0.39L6.39 0.349999H9.54ZM7.92 2.69L4.92 13.16H10.89L7.92 2.69ZM22.2438 5C23.9638 5 25.2138 5.39 25.9938 6.17C26.7938 6.93 27.1938 8.15 27.1938 9.83V20H24.7638V17.66C23.6038 19.46 22.2938 20.36 20.8338 20.36C19.6738 20.36 18.7438 19.96 18.0438 19.16C17.3638 18.36 17.0238 17.27 17.0238 15.89V15.05C17.0238 13.85 17.4338 12.86 18.2538 12.08C19.0738 11.28 20.1738 10.88 21.5538 10.88C22.3138 10.88 23.3438 11.01 24.6438 11.27V9.62C24.6438 8.76 24.4438 8.14 24.0438 7.76C23.6638 7.38 23.0638 7.19 22.2438 7.19C21.2838 7.19 19.9338 7.34 18.1938 7.64V5.45C20.0738 5.15 21.4238 5 22.2438 5ZM22.1838 12.86C21.3438 12.86 20.7038 13.08 20.2638 13.52C19.8238 13.96 19.6038 14.58 19.6038 15.38V15.71C19.6038 16.45 19.7738 17.03 20.1138 17.45C20.4538 17.87 20.8838 18.08 21.4038 18.08C21.9438 18.08 22.4938 17.85 23.0538 17.39C23.6338 16.91 24.1638 16.24 24.6438 15.38V13.07C23.8838 12.93 23.0638 12.86 22.1838 12.86Z"
                            fill="#666666"
                        />
                    </svg>
                )
            }
        ],
        advanced: true
    },
    fontSize: {
        stateName: 'currentItemFontSize',
        supportedElementTypes: ['text'],
        buttonSx: {
            padding: '6px 0',
            minWidth: '50px'
        },
        buttons: [
            {
                value: 16,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="25" viewBox="0 0 14 19" fill="none">
                        <path
                            d="M0.078125 12.4805L2.22266 12.293C2.32422 13.1523 2.55859 13.8594 2.92578 14.4141C3.30078 14.9609 3.87891 15.4062 4.66016 15.75C5.44141 16.0859 6.32031 16.2539 7.29688 16.2539C8.16406 16.2539 8.92969 16.125 9.59375 15.8672C10.2578 15.6094 10.75 15.2578 11.0703 14.8125C11.3984 14.3594 11.5625 13.8672 11.5625 13.3359C11.5625 12.7969 11.4062 12.3281 11.0938 11.9297C10.7812 11.5234 10.2656 11.1836 9.54688 10.9102C9.08594 10.7305 8.06641 10.4531 6.48828 10.0781C4.91016 9.69531 3.80469 9.33594 3.17188 9C2.35156 8.57031 1.73828 8.03906 1.33203 7.40625C0.933594 6.76562 0.734375 6.05078 0.734375 5.26172C0.734375 4.39453 0.980469 3.58594 1.47266 2.83594C1.96484 2.07812 2.68359 1.50391 3.62891 1.11328C4.57422 0.722656 5.625 0.527344 6.78125 0.527344C8.05469 0.527344 9.17578 0.734375 10.1445 1.14844C11.1211 1.55469 11.8711 2.15625 12.3945 2.95312C12.918 3.75 13.1992 4.65234 13.2383 5.66016L11.0586 5.82422C10.9414 4.73828 10.543 3.91797 9.86328 3.36328C9.19141 2.80859 8.19531 2.53125 6.875 2.53125C5.5 2.53125 4.49609 2.78516 3.86328 3.29297C3.23828 3.79297 2.92578 4.39844 2.92578 5.10938C2.92578 5.72656 3.14844 6.23438 3.59375 6.63281C4.03125 7.03125 5.17188 7.44141 7.01562 7.86328C8.86719 8.27734 10.1367 8.64062 10.8242 8.95312C11.8242 9.41406 12.5625 10 13.0391 10.7109C13.5156 11.4141 13.7539 12.2266 13.7539 13.1484C13.7539 14.0625 13.4922 14.9258 12.9688 15.7383C12.4453 16.543 11.6914 17.1719 10.707 17.625C9.73047 18.0703 8.62891 18.293 7.40234 18.293C5.84766 18.293 4.54297 18.0664 3.48828 17.6133C2.44141 17.1602 1.61719 16.4805 1.01562 15.5742C0.421875 14.6602 0.109375 13.6289 0.078125 12.4805Z"
                            fill="#666666"
                        />
                    </svg>
                )
            },
            {
                value: 20,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="25" viewBox="0 0 18 18" fill="none">
                        <path
                            d="M0.78125 18V0.820312H4.20312L8.26953 12.9844C8.64453 14.1172 8.91797 14.9648 9.08984 15.5273C9.28516 14.9023 9.58984 13.9844 10.0039 12.7734L14.1172 0.820312H17.1758V18H14.9844V3.62109L9.99219 18H7.94141L2.97266 3.375V18H0.78125Z"
                            fill="#666666"
                        />
                    </svg>
                )
            },
            {
                value: 28,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="25" viewBox="0 0 12 18" fill="none">
                        <path d="M0.757812 18V0.820312H3.03125V15.9727H11.4922V18H0.757812Z" fill="#666666" />
                    </svg>
                )
            },
            {
                value: 36,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="29" height="25" viewBox="0 0 29 18" fill="none">
                        <path
                            d="M0.105469 18L6.75 9.04688L0.890625 0.820312H3.59766L6.71484 5.22656C7.36328 6.14062 7.82422 6.84375 8.09766 7.33594C8.48047 6.71094 8.93359 6.05859 9.45703 5.37891L12.9141 0.820312H15.3867L9.35156 8.91797L15.8555 18H13.043L8.71875 11.8711C8.47656 11.5195 8.22656 11.1367 7.96875 10.7227C7.58594 11.3477 7.3125 11.7773 7.14844 12.0117L2.83594 18H0.105469ZM17.8656 18V0.820312H20.1391V15.9727H28.6V18H17.8656Z"
                            fill="#666666"
                        />
                    </svg>
                )
            }
        ],
        advanced: true
    },
    textAlign: {
        stateName: 'currentItemTextAlign',
        supportedElementTypes: ['text'],
        buttons: [
            {
                value: 'left',
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 24 22" fill="none">
                        <rect width="24" height="4" transform="matrix(1 0 0 -1 0 22)" fill="#666666" />
                        <rect width="16" height="4" transform="matrix(1 0 0 -1 0 16)" fill="#666666" />
                        <rect width="24" height="4" transform="matrix(1 0 0 -1 0 10)" fill="#666666" />
                        <rect width="16" height="4" transform="matrix(1 0 0 -1 0 4)" fill="#666666" />
                    </svg>
                )
            },
            {
                value: 'center',
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 24 22" fill="none">
                        <rect width="24" height="4" transform="matrix(1 0 0 -1 0 22)" fill="#666666" />
                        <rect width="16" height="4" transform="matrix(1 0 0 -1 4 16)" fill="#666666" />
                        <rect width="24" height="4" transform="matrix(1 0 0 -1 0 10)" fill="#666666" />
                        <rect width="16" height="4" transform="matrix(1 0 0 -1 4 4)" fill="#666666" />
                    </svg>
                )
            },
            {
                value: 'right',
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 24 22" fill="none">
                        <rect width="24" height="4" transform="matrix(1 0 0 -1 0 22)" fill="#666666" />
                        <rect width="16" height="4" transform="matrix(1 0 0 -1 8 16)" fill="#666666" />
                        <rect width="24" height="4" transform="matrix(1 0 0 -1 0 10)" fill="#666666" />
                        <rect width="16" height="4" transform="matrix(1 0 0 -1 8 4)" fill="#666666" />
                    </svg>
                )
            }
        ],
        advanced: true
    },
    roughness: {
        stateName: 'currentItemRoughness',
        buttons: [
            {
                value: 0,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 40 20" fill="none">
                        <path
                            d="M3.00098 16.1691C6.28774 13.9744 19.6399 2.8905 22.7215 3.00082C25.8041 3.11113 19.1158 15.5488 21.4962 16.8309C23.8757 18.1131 34.4155 11.7148 37.0001 10.6919"
                            stroke="#666666"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>
                )
            },
            {
                value: 1,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 40 20" fill="none">
                        <path
                            d="M6.40706 14.9999C13 11.1917 20.0468 1.51033 23.0234 3.00508C26 4.49983 20.457 12.8658 22.7285 16.4328C25 19.9999 36.3757 12.9999 36.3757 12.9999M3 16.9999C6.68158 14.8751 16.1296 9.09837 22.0648 6.5491C28 3.99983 22.2896 13.3208 25 13.9999C27.7104 14.679 36.3757 9.64698 36.3757 9.64698L3 16.9999Z"
                            stroke="#666666"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>
                )
            },
            {
                value: 2,
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 40 20" fill="none">
                        <path
                            d="M3.97143 10.4917C6.61158 9.24563 16.3706 2.61886 19.8104 3.01724C23.2522 3.41472 22.0773 12.2013 24.6181 12.8783C27.1598 13.5536 33.3179 8.04068 35.0571 7.07244M3 15.6468C6.93692 13.5378 22.5544 2.81528 26.6206 3.00242C30.6877 3.18956 25.6708 15.3346 27.4009 16.7705C29.1309 18.2055 35.4001 12.4762 37 11.6177L3 15.6468Z"
                            stroke="#666666"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>
                )
            }
        ],
        supportedElementTypes: ['rectangle', 'diamond', 'ellipse', 'arrow', 'line'],
        advanced: true
    },
    strokeSharpness: {
        stateName: 'currentItemStrokeSharpness',
        supportedElementTypes: ['rectangle', 'arrow', 'line'],
        buttons: [
            {
                value: 'sharp',
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 34 26" fill="none">
                        <path d="M2 26L2 2L34 2" stroke="#666666" strokeWidth="3" />
                    </svg>
                )
            },
            {
                value: 'round',
                icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 34 26" fill="none">
                        <path d="M2 26L2 17C2 8.71573 8.71573 2 17 2L34 2" stroke="#666666" strokeWidth="3" />
                    </svg>
                )
            }
        ],
        advanced: true
    }
};

/**
 *
 * @param {string} elementType
 * @param {string} optionName
 */
const isOptionSupported = (showAdvancedOptions: boolean, elementType: string, optionName: string) =>
    (showAdvancedOptions || !optionsInfo[optionName].advanced) &&
    optionsInfo[optionName] &&
    (!optionsInfo[optionName].supportedElementTypes ||
        optionsInfo[optionName].supportedElementTypes!.includes(elementType));

const optionSpacing = 1;

type Props = {
    show: boolean;
    excalidrawAPI: ExcalidrawImperativeAPI;
    selectedElementIds: string[];
    activeTool: AppState['activeTool'];
    onGridModeChange: (enabled: boolean) => void;
    gridModeEnabled: boolean;
    libraryGroups: LibraryGroup[];
    onToolTypeChange: (newToolType: string) => void;
} & LayerButtonProps;

export default function ElementOptionsPanel(props: Props) {
    const { t } = useTranslation(['whiteboard', 'common']);
    const { userInfo, uploadImage, saveElements, getElements, deleteElements } = useContext(WhiteboardContext);
    const [showAdvancedOptions, setShowAdvancedOptions] = useState<boolean>(false);
    const [hasAdvancedOptions, setHasAdvancedOptions] = useState<boolean>(false);

    const { whiteboardSessionState, excalidrawAPIRef, setToolProperties } = useContext(WhiteboardSessionContext);

    const { presenter } = whiteboardSessionState;

    const { show, libraryGroups, excalidrawAPI, selectedElementIds, activeTool } = props;
    const classes = useStyles();
    const previousActiveTool = usePrevious(activeTool);

    useEffect(() => {
        if (activeTool !== previousActiveTool) {
            if (['screenshot', 'library'].includes(activeTool.type)) {
                setHasAdvancedOptions(false);
            } else {
                setHasAdvancedOptions(true);
            }
            setShowAdvancedOptions(false);
        }
    }, [activeTool, previousActiveTool, setShowAdvancedOptions, setHasAdvancedOptions]);

    const [currentElementType, setCurrentElementType] = useState<string>('selection');
    const [elementOptionValues, setElementOptionValues] = useState<{ [x: string]: any }>({
        backgroundColor: 'transparent',
        strokeColor: '#000000',
        opacity: 100,
        fillStyle: 'hachure',
        strokeWidth: 1,
        strokeStyle: 'solid',
        strokeSharpness: 'sharp',
        fontFamily: 1,
        fontSize: 16,
        textAlign: 'left',
        roughness: 1
    });
    const [elementsLocked, setElementsLocked] = useState<boolean>(false);
    const [activeUserNames, setActiveUserNames] = useState<string[]>([]);
    const [screenShareData, setScreenShareData] = useState<any>({});

    interface CopyPositionCounter {
        [id: string]: number;
    }

    const [copyPositionCount, setCopyPositionCount] = useState<CopyPositionCounter>({});

    useEffect(() => {
        const newToolProperties: any = {};
        if (presenter && userInfo && presenter.student_id === userInfo.student_id) {
            newToolProperties.present = {
                border: '0.3rem solid #ffba00',
                padding: '0.45rem'
            };
        }
        if (screenShareData && screenShareData.mediaStream) {
            newToolProperties.screenshot = {
                border: '0.3rem solid #ffba00',
                padding: '0.45rem'
            };
        }
        setToolProperties(newToolProperties);
    }, [presenter, screenShareData, setToolProperties, userInfo]);

    useEffect(() => {
        setCurrentElementType(activeTool.type);
    }, [activeTool, screenShareData]);

    useEffect(() => {
        if (props.selectedElementIds.length > 0) {
            const selectedElements = props.excalidrawAPI
                .getSceneElements()
                .filter((element) => props.selectedElementIds.includes(element.id));
            const firstElement = selectedElements[0];

            const userNames = _.uniq(
                selectedElements.map((element) => element.customData?.student?.user_name).filter((name) => name)
            );
            setActiveUserNames(userNames);

            if (firstElement) {
                setElementOptionValues((prev) => {
                    // Shallow copy object for new state.
                    const newState = { ...prev };
                    Object.keys(optionsInfo).forEach((propertyName) => {
                        // @ts-ignore
                        newState[propertyName] = firstElement[propertyName];
                    });
                    return newState;
                });

                setCurrentElementType(firstElement.type);
                setElementsLocked(firstElement.locked);
            }
        } else {
            setActiveUserNames([]);
        }
    }, [props.excalidrawAPI, props.selectedElementIds, setActiveUserNames]);

    const handleChange = (property: string, newValue: any) => {
        changeAppState(props.excalidrawAPI, optionsInfo[property].stateName, newValue);
        changeElements(props.excalidrawAPI, property, newValue);
        setElementOptionValues((prev) => {
            // Shallow copy object for new state.
            const newState = { ...prev };
            newState[property] = newValue;
            return newState;
        });
    };

    const returnCopyPosition = useCallback((element: NonDeletedExcalidrawElement) => {
        const x = Math.floor(element.x);
        const y = Math.floor(element.y);

        return `${x}:${y}`;
    }, []);

    const handleCopy = useCallback(() => {
        if (!selectedElementIds.length) {
            return;
        }

        const nextCopyCounter: CopyPositionCounter = {};

        const copiedElements = excalidrawAPI
            .getSceneElements()
            .filter((e) => selectedElementIds.includes(e.id))
            .map((e) => {
                const copyId = returnCopyPosition(e);

                const count = copyPositionCount[copyId] ? copyPositionCount[copyId] + 1 : 1;

                nextCopyCounter[copyId] = nextCopyCounter[copyId] ? nextCopyCounter[copyId] + 1 : 1;

                const copyOffset = 20 * count;

                return { ...e, id: nanoid(), x: e.x + copyOffset, y: e.y + copyOffset };
            });

        setCopyPositionCount((previous) => {
            const result = _.mergeWith(previous, nextCopyCounter, (a, b) => {
                if (_.isNumber(a) && _.isNumber(b)) {
                    return a + b;
                }
            });

            return result;
        });

        excalidrawAPI.updateScene({
            elements: [...excalidrawAPI.getSceneElements(), ...copiedElements]
        });
    }, [selectedElementIds, excalidrawAPI, copyPositionCount, returnCopyPosition, setCopyPositionCount]);

    const handleDelete = () => {
        if (!selectedElementIds.length) {
            return;
        }

        excalidrawAPI.updateScene({
            elements: excalidrawAPI
                .getSceneElements()
                .map((e) =>
                    selectedElementIds.includes(e.id)
                        ? { ...e, isDeleted: !e.customData?.pdfPage, version: e.version + 1 }
                        : e
                )
        });
    };

    const handleLockUnlock = () => {
        if (!selectedElementIds.length) {
            return;
        }

        excalidrawAPI.updateScene({
            elements: excalidrawAPI
                .getSceneElements()
                .map((e) =>
                    selectedElementIds.includes(e.id) ? { ...e, locked: !elementsLocked, version: e.version + 1 } : e
                )
        });
        setElementsLocked(!elementsLocked);
    };

    const handleSelectScreen = useCallback(() => {
        const selectScreen = async () => {
            const newScreenShareData: any = {};
            try {
                newScreenShareData.mediaStream = await navigator.mediaDevices.getDisplayMedia({
                    video: true,
                    audio: false
                });
                // eslint-disable-next-line prefer-destructuring
                newScreenShareData.shareScreenTrack = newScreenShareData.mediaStream.getVideoTracks()[0];
                newScreenShareData.shareScreenTrack.addEventListener('ended', () => {});
                newScreenShareData.shareScreenCanvas = document.createElement('canvas');
                newScreenShareData.shareScreenVideo = document.createElement('video');
                await new Promise((resolve) => {
                    newScreenShareData.shareScreenVideo.onloadedmetadata = () => {
                        newScreenShareData.shareScreenVideo.play();
                        resolve(null);
                    };
                    newScreenShareData.shareScreenVideo.srcObject = newScreenShareData.mediaStream;
                });

                console.log('shareScreenVideo', newScreenShareData.shareScreenVideo);
                const width = newScreenShareData.shareScreenVideo.videoWidth;
                const height = newScreenShareData.shareScreenVideo.videoHeight;
                newScreenShareData.shareScreenCanvas.width = width;
                newScreenShareData.shareScreenCanvas.height = height;
                newScreenShareData
                    .shareScreenCanvas!.getContext('2d')!
                    .drawImage(newScreenShareData.shareScreenVideo, 0, 0, width, height);

                const currentPage = whiteboardSessionState.currentPage!;
                const allElements = await getElements(currentPage);
                const screenshotElements = allElements.filter((element) => element.body?.customData?.isScreenShot);
                let maxY = 0;
                screenshotElements.forEach((element) => {
                    maxY = Math.max(element.body.y + element.body.height + 100);
                });
                newScreenShareData.shareScreenCanvas.width = width;
                newScreenShareData.shareScreenCanvas.height = height;
                newScreenShareData
                    .shareScreenCanvas!.getContext('2d')!
                    .drawImage(newScreenShareData.shareScreenVideo, 0, 0, width, height);

                const blobUrl = await newScreenShareData.shareScreenCanvas.toDataURL('image/png');
                const fileId = uuid();

                const file: BinaryFileData = {
                    mimeType: 'image/png',
                    created: Date.now(),
                    id: fileId as FileId,
                    dataURL: blobUrl as DataURL
                };
                try {
                    await uploadImage(currentPage, file);

                    if (screenShareData.replaceOldScreenshots) {
                        maxY = 0;
                    }

                    const element: ImageElementWithKey = {
                        type: 'image',
                        x: 0,
                        y: maxY,
                        angle: 0,
                        backgroundColor: 'transparent',
                        boundElements: null,
                        fillStyle: 'hachure',
                        groupIds: [],
                        isDeleted: false,
                        link: null,
                        locked: false,
                        opacity: 100,
                        roughness: 1,
                        roundness: { type: 1 },
                        seed: 0,
                        status: 'saved',
                        strokeColor: 'transparent',
                        strokeStyle: 'solid',
                        strokeWidth: 1,
                        version: 1,
                        versionNonce: 1,
                        scale: [1, 1],
                        width,
                        height,
                        id: fileId,
                        fileId: fileId as FileId,
                        key: `${fileId}.png`,
                        updated: file.created,
                        customData: {
                            isScreenShot: true
                        }
                    };
                    await saveElements(currentPage, [element], false);
                    if (newScreenShareData.replaceOldScreenshots && screenshotElements.length > 0) {
                        await deleteElements(screenshotElements.map((e) => e.body.id));
                    }

                    setTimeout(async () => {
                        const elements = await getElements(currentPage);
                        excalidrawAPIRef.current!.updateScene({
                            elements: elements.map((e) => e.body),
                            commitToHistory: true
                        });
                    }, 1000);
                } catch (err) {
                    console.error(err);
                }

                newScreenShareData.mediaStream.getTracks().forEach((track) => track.stop());
                setScreenShareData({});
            } catch (err) {
                console.error(err);
            }
        };
        selectScreen();
    }, [
        whiteboardSessionState.currentPage,
        getElements,
        uploadImage,
        screenShareData.replaceOldScreenshots,
        saveElements,
        deleteElements,
        excalidrawAPIRef
    ]);

    const libraryRendered = useMemo(
        () => (
            <Library
                libraryGroups={libraryGroups}
                excalidrawAPI={props.excalidrawAPI}
                show={currentElementType === 'library'}
            />
        ),
        [currentElementType, libraryGroups, props.excalidrawAPI]
    );

    return (
        <>
            <Box
                m={1}
                sx={{
                    overflowY: 'auto',
                    width: '230px',
                    maxWidth: '100%',
                    flexShrink: 0,
                    maxHeight: 'calc(100% - 150px)'
                }}
            >
                <Box
                    style={{
                        overflowX: 'hidden',
                        width: '214px'
                    }}
                >
                    {currentElementType === 'screenshot' && (
                        <ElementOption label={t('common:actions')}>
                            <Stack spacing={1} direction="column">
                                <Box>
                                    <Button variant="contained" color="inherit" onClick={handleSelectScreen}>
                                        {t('whiteboard:post-screenshot')}
                                    </Button>
                                </Box>
                            </Stack>
                        </ElementOption>
                    )}

                    {isOptionSupported(showAdvancedOptions, currentElementType, 'strokeColor') &&
                        isOptionSupported(showAdvancedOptions, currentElementType, 'backgroundColor') && (
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Box
                                    style={{
                                        width: '105px'
                                    }}
                                >
                                    <ColorPickerOption
                                        allColors={standardStrokeColors}
                                        currentColor={elementOptionValues.strokeColor}
                                        onChange={(color) => handleChange('strokeColor', color.hex)}
                                        label={t('whiteboard:strokeColor')}
                                    />
                                </Box>
                                <Box
                                    style={{
                                        width: '105px'
                                    }}
                                >
                                    <ColorPickerOption
                                        allColors={standardBackgroundColors}
                                        currentColor={elementOptionValues.backgroundColor}
                                        onChange={(color) => handleChange('backgroundColor', color.hex)}
                                        label={t('whiteboard:background')}
                                    />
                                </Box>
                            </Box>
                        )}

                    {currentElementType === 'library' && (
                        <>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={props.gridModeEnabled}
                                        onChange={(event) => props.onGridModeChange(event.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={t('whiteboard:showGrid') as string}
                            />
                            <Box
                                style={{
                                    maxHeight: '250px',
                                    overflowY: 'auto'
                                }}
                            >
                                {libraryRendered}
                            </Box>
                        </>
                    )}

                    {isOptionSupported(showAdvancedOptions, currentElementType, 'strokeColor') &&
                        !isOptionSupported(showAdvancedOptions, currentElementType, 'backgroundColor') && (
                            <Box
                                style={{
                                    width: '214px'
                                }}
                            >
                                <ColorPickerOption
                                    allColors={standardStrokeColors}
                                    currentColor={elementOptionValues.strokeColor}
                                    onChange={(color) => handleChange('strokeColor', color.hex)}
                                    label={t('whiteboard:strokeColor')}
                                />
                            </Box>
                        )}

                    {isOptionSupported(showAdvancedOptions, currentElementType, 'backgroundColor') &&
                        !isOptionSupported(showAdvancedOptions, currentElementType, 'strokeColor') && (
                            <Box
                                style={{
                                    width: '214px'
                                }}
                            >
                                <ColorPickerOption
                                    allColors={standardBackgroundColors}
                                    currentColor={elementOptionValues.backgroundColor}
                                    onChange={(color) => handleChange('backgroundColor', color.hex)}
                                    label={t('whiteboard:backgroundColor')}
                                />
                            </Box>
                        )}

                    {['fillStyle']
                        .filter((optionName) => isOptionSupported(showAdvancedOptions, currentElementType, optionName))
                        .map((optionName) => (
                            <ElementOptionButtons
                                key={optionName}
                                label={t(`whiteboard:${optionName}`)}
                                propertyName={optionName}
                                currentValue={elementOptionValues[optionName]}
                                handleChange={handleChange}
                            />
                        ))}

                    {userInfo?.role?.toLowerCase() !== 'student' && activeUserNames.length > 0 && (
                        <ElementOption label={t('whiteboard:userNames')}>
                            <Box>
                                {activeUserNames.length > 5
                                    ? `${activeUserNames.length} ${t('whiteboard:users')}`
                                    : activeUserNames.join(', ')}
                            </Box>
                        </ElementOption>
                    )}

                    {hasAdvancedOptions && !showAdvancedOptions && (
                        <ShowAdvancedOptionsButton onClick={() => setShowAdvancedOptions(true)}>
                            {t('whiteboard:show-advanced-options')}
                            <ShowAdvancedOptionsButtonIcon />
                        </ShowAdvancedOptionsButton>
                    )}
                    {hasAdvancedOptions && showAdvancedOptions && (
                        <HideAdvancedOptionsButton onClick={() => setShowAdvancedOptions(false)}>
                            {t('whiteboard:hide-advanced-options')}
                            <HideAdvancedOptionsButtonIcon />
                        </HideAdvancedOptionsButton>
                    )}

                    {isOptionSupported(showAdvancedOptions, currentElementType, 'opacity') && (
                        <Box
                            style={{
                                width: '214px'
                            }}
                        >
                            <ElementOption label={t('whiteboard:opacity')}>
                                <Box pr={2}>
                                    <Slider
                                        onChange={(event, newValue) => handleChange('opacity', newValue as number)}
                                        defaultValue={100}
                                        value={elementOptionValues.opacity}
                                        step={10}
                                        marks
                                    />
                                </Box>
                            </ElementOption>
                        </Box>
                    )}

                    {[
                        'fontFamily',
                        'fontSize',
                        'textAlign',
                        'strokeWidth',
                        'strokeStyle',
                        'roughness',
                        'strokeSharpness'
                    ]
                        .filter((optionName) => isOptionSupported(showAdvancedOptions, currentElementType, optionName))
                        .map((optionName) => (
                            <ElementOptionButtons
                                key={optionName}
                                label={t(`whiteboard:${optionName}`)}
                                propertyName={optionName}
                                currentValue={elementOptionValues[optionName]}
                                handleChange={handleChange}
                            />
                        ))}

                    {showAdvancedOptions && selectedElementIds.length > 0 && <LayerButtons {...props} />}

                    {showAdvancedOptions && props.selectedElementIds.length > 0 && (
                        <ElementOption label={t('common:actions')}>
                            <Stack spacing={1} direction="row">
                                <Button variant="contained" color="inherit" onClick={handleCopy}>
                                    {t('common:copy')}
                                </Button>
                                <Button variant="contained" color="inherit" onClick={handleDelete}>
                                    {t('common:delete')}
                                </Button>
                                <Button variant="contained" color="inherit" onClick={handleLockUnlock}>
                                    {!elementsLocked && <>{t('whiteboard:lock')}</>}
                                    {elementsLocked && <>{t('whiteboard:unlock')}</>}
                                </Button>
                            </Stack>
                        </ElementOption>
                    )}
                </Box>
            </Box>
        </>
    );
}

function ElementOption({ children, label }: { children: ReactNode; label?: string }) {
    return (
        <Box
            style={{
                marginBottom: '3px'
            }}
        >
            {label && <Typography variant="body2">{label}</Typography>}
            {children}
        </Box>
    );
}

function ElementOptionButtons({
    propertyName,
    label,
    currentValue,
    handleChange
}: {
    propertyName: string;
    label: string;
    currentValue: any;
    handleChange: (property: string, newValue: any) => void;
}) {
    if (!optionsInfo[propertyName].buttons || optionsInfo[propertyName].buttons === undefined) {
        throw new WhiteboardError(
            `${CODING_ERROR_OPTION_NOT_SUPPORTED}: Coding error: Option does not support buttons: ${propertyName}`
        );
    }

    return (
        <ElementOption label={label}>
            <Stack spacing={1} direction="row">
                {optionsInfo[propertyName].buttons!.map((button: { value: any; icon: string }, index) => (
                    <Button
                        sx={optionsInfo[propertyName].buttonSx || {}}
                        className="option-button"
                        key={index}
                        variant="contained"
                        color={currentValue === button.value ? 'primary' : 'inherit'}
                        onClick={() => handleChange(propertyName, button.value)}
                    >
                        {button.icon}
                    </Button>
                ))}
            </Stack>
        </ElementOption>
    );
}

function ColorPickerOption(props: ColorPickerButtonProps & { label: string }) {
    return (
        <ElementOption label={props.label}>
            <ColorPickerButton {...props} />
        </ElementOption>
    );
}
